// /opt/isp_erp/frontend/src/pages/ftth/NapsList.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Box,
  Button,
  CircularProgress,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Grid,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  SignalCellularAlt as SignalIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { ftthAPI } from '../../api/ftth.api';
import { useNavigate } from 'react-router-dom';
import axios from '../../api/axios';

const NapsList = () => {
  const [naps, setNaps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Estados para el diálogo de clientes
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedNap, setSelectedNap] = useState(null);
  const [clientesNap, setClientesNap] = useState({ clientes: [], total_clientes: 0, nap: null });
  const [loadingClientes, setLoadingClientes] = useState(false);

  useEffect(() => {
    cargarNaps();
  }, []);

  const cargarNaps = async () => {
    setLoading(true);
    try {
      const data = await ftthAPI.getNaps();
      setNaps(data);
    } catch (error) {
      console.error('Error:', error);
      setError('Error al cargar NAPs');
    } finally {
      setLoading(false);
    }
  };

  const verClientesNap = async (nap) => {
    setSelectedNap(nap);
    setOpenDialog(true);
    setLoadingClientes(true);
    try {
      const response = await axios.get(`/ftth/naps/${nap.id}/clientes`);
      setClientesNap(response.data);
    } catch (error) {
      console.error('Error cargando clientes del NAP:', error);
      setClientesNap({ clientes: [], total_clientes: 0, nap: null });
    } finally {
      setLoadingClientes(false);
    }
  };

  const getEstadoColor = (estado) => {
    switch(estado) {
      case 'ACTIVO': return 'success';
      case 'LLENO': return 'warning';
      case 'MANTENIMIENTO': return 'info';
      case 'INACTIVO': return 'error';
      default: return 'default';
    }
  };

  const getOcupacionColor = (porcentaje) => {
    if (porcentaje >= 90) return '#f44336';
    if (porcentaje >= 70) return '#ff9800';
    return '#4caf50';
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">📋 Lista de NAPs</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/ftth/naps/nuevo')}>
          Agregar NAP
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell>Código</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Dirección</TableCell>
                <TableCell>Zona/Sector</TableCell>
                <TableCell>Puertos</TableCell>
                <TableCell>Ocupación</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="center">Clientes</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {naps.map((nap) => (
                <TableRow key={nap.id} hover>
                  <TableCell><strong>{nap.codigo}</strong></TableCell>
                  <TableCell>{nap.nombre}</TableCell>
                  <TableCell>{nap.direccion}</TableCell>
                  <TableCell>{nap.zona} / {nap.sector}</TableCell>
                  <TableCell>{nap.puertos_ocupados}/{nap.capacidad_puertos}</TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LinearProgress
                        variant="determinate"
                        value={nap.porcentaje_ocupacion}
                        sx={{
                          flexGrow: 1,
                          height: 8,
                          borderRadius: 3,
                          bgcolor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': { bgcolor: getOcupacionColor(nap.porcentaje_ocupacion) }
                        }}
                      />
                      <Typography variant="caption">{nap.porcentaje_ocupacion}%</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={nap.estado} color={getEstadoColor(nap.estado)} size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Ver clientes conectados">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => verClientesNap(nap)}
                        sx={{ minWidth: 'auto', px: 1 }}
                      >
                        {nap.puertos_ocupados}
                      </Button>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" color="primary"><EditIcon /></IconButton>
                    <IconButton size="small" color="error"><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Diálogo para mostrar clientes del NAP */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" component="div">
                📡 Clientes conectados a {selectedNap?.codigo}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedNap?.nombre} - {selectedNap?.direccion}
              </Typography>
            </Box>
            <IconButton onClick={() => setOpenDialog(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent>
          {loadingClientes ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : clientesNap.clientes?.length > 0 ? (
            <>
              <Box mb={2} p={2} sx={{ bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Total clientes:
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {clientesNap.total_clientes}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Ocupación:
                    </Typography>
                    <Typography variant="h6">
                      {clientesNap.nap?.puertos_ocupados}/{clientesNap.nap?.capacidad_puertos}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <List>
                {clientesNap.clientes.map((cliente, index) => (
                  <React.Fragment key={cliente.id}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#3b82f6' }}>
                          <PersonIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box>
                            <Typography variant="subtitle1" component="span">
                              {cliente.nombre_completo}
                            </Typography>
                            <Chip
                              label={`Puerto ${cliente.puerto}`}
                              size="small"
                              color="primary"
                              sx={{ ml: 1 }}
                            />
                          </Box>
                        }
                        secondary={
                          <Grid container spacing={1} mt={0.5}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="caption" display="block" color="text.secondary">
                                📄 ID: {cliente.identificacion}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant="caption" display="block" color="text.secondary">
                                📞 Tel: {cliente.telefono || 'No registrado'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="caption" display="block" color="text.secondary">
                                📍 {cliente.direccion || 'Sin dirección'}
                              </Typography>
                            </Grid>
                            {cliente.nivel_senal_rx && (
                              <Grid item xs={12}>
                                <Typography variant="caption" display="block" color="text.secondary">
                                  <SignalIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} />
                                  Nivel de señal: {cliente.nivel_senal_rx} dBm
                                </Typography>
                              </Grid>
                            )}
                            {cliente.fecha_asignacion && (
                              <Grid item xs={12}>
                                <Typography variant="caption" display="block" color="text.secondary">
                                  📅 Asignado: {new Date(cliente.fecha_asignacion).toLocaleDateString('es-ES')}
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        }
                      />
                    </ListItem>
                    {index < clientesNap.clientes.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </>
          ) : (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="text.secondary">
                📭 No hay clientes conectados a este NAP
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} variant="outlined">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default NapsList;
